<template>
  <HeaderNavbar :title="$t('settings.companies.title')" :breadcrumbs="listBreadcrumbs" noBottomMargin />
  <SidebarLayout>
    <template #search>
      <LiFormInput v-model="searchQuery" icon="ti ti-search" :placeholder="$t('search')" />
    </template>
    <template #sidebar>
      <BaseTree
        class="companies-tree"
        v-model="hierarchy"
        treeLine
        updateBehavior="new"
        ref="treeDataRef"
        :treeLineOffset="36"
        :statHandler="
          (stat: any) => {
            stat.class = `tree-level-${stat.level}`;
            return stat;
          }
        "
        @click:node="clickedCompany">
        <template #default="{ node }">
          <div
            class="company-block gap-2"
            :class="{ active: route.params.id == node.id }"
            data-testid="companies-tree-node"
            :data-company-id="node.id">
            <div
              v-if="node.logo_image?.data"
              class="company-logo avatar"
              :style="`background-image: url('${node.logo_image?.data.proxy.url_thumbnail}'`" />
            <div class="company-logo-placeholder avatar d-flex align-items-center justify-content-center" v-else>
              <IconBuilding :size="18" :stroke-width="1.5" class="text-muted" />
            </div>
            <div class="d-flex flex-column" style="width: 80%">
              <h4 class="m-0 text-truncate">{{ node.name }}</h4>
              <p class="m-0 text-truncate">
                <small>{{ companyAddress(node) }}</small>
              </p>
            </div>
          </div>
        </template>
      </BaseTree>
    </template>
    <router-view @refetchCompanies="getCompanies" :key="companyId" />
  </SidebarLayout>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { pushAndWait } from '@/autopilot/functions/navigate';
import { buildPjasUri } from '@/func/entityLinks';
import { companyWorkflowGuide } from '@/autopilot/functions/companies/companyWorkflow';
import { companyFields } from '@/autopilot/functions/companies/companyFields';
import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import { FormInput as LiFormInput } from '@prospective/lithium';
import SidebarLayout from '@/components/common/SidebarLayout.vue';

import { IconBuilding } from '@tabler/icons-vue';
import type { ICompany } from '@/types/company';
import { BaseTree } from '@he-tree/vue';
import '@he-tree/vue/style/default.css';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';

import translator from '@/locale/translator';
import { fetchCompanies, createCompany } from '@/api/company';
import { debounce } from '@prospective/lithium';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';

const router = useRouter();
const route = useRoute();

const listBreadcrumbs = [
  { label: translator.get('nav.settings'), path: { name: 'settings' } },
  { label: translator.get('settings.companies.title'), path: { name: 'settings.companies' } },
];

/* --------------------------------- STATE -------------------------------- */
const companies = ref<ICompany[]>([]);
const searchQuery = ref('');

const debouncedGetCompanies = debounce(getCompanies, 400);

onMounted(() => {
  getCompanies();
});

/* -------------------------------- COMPUTED -------------------------------- */
const companyId = computed(() => route.params.id as string | undefined);
const hierarchy = computed(() => {
  if (!companies.value?.length) return [];
  return companies.value;
});

/* ---------------------------------- WATCH --------------------------------- */
watch(searchQuery, () => {
  debouncedGetCompanies();
});

/* ------------------------------- METHODS ------------------------------- */
async function getCompanies() {
  try {
    const params = {
      tree_type: 'tree',
      search: searchQuery.value,
      resolve: 'logo_image',
    };
    const response = (await fetchCompanies(params)) as { data: ICompany[] };

    companies.value = response.data;
    nextTick(() => selectFirstCompany(companies.value));
  } catch (error) {
    console.error('Error fetching companies:', error);
  } finally {
    routeReady();
  }
}
function companyAddress(company: ICompany) {
  return `${company.address_street || ''} ${company.address_city || ''} ${company.address_zip || ''} ${company.address_country || ''}`;
}

function selectFirstCompany(companies: ICompany[]) {
  if (companies.length > 0 && !companyId.value) clickedCompany({ data: companies[0] });
}

function clickedCompany(stat: { data: ICompany }) {
  if (!stat.data?.id) {
    useToast().error(translator.get('settings.companies.error_selecting_company'));
    return router.replace({ name: 'settings.companies' });
  }
  router.push({ name: 'settings.company.details', params: { id: stat.data.id } });
}

/* -------------------------------- AUTOPILOT ------------------------------- */
const routeReady = useAutopilotRouteReady();
useAutopilotContext('company_workflow', () => companyWorkflowGuide);
useAutopilotContext('company_list', () => ({
  description:
    'Companies belonging to the current customer. Each item has a pjas://company:<id> link. Search is the current tree search box.',
  selectedCompanyId: companyId.value ?? null,
  search: searchQuery.value,
  items: companies.value.map((c) => ({
    id: c.id,
    name: c.name,
    address: companyAddress(c),
    parent_company_id: c.parent_company_id ?? null,
    link: buildPjasUri('company', c.id),
  })),
}));

const setCompanySearch: AutopilotFunctionDefinition = {
  name: 'set_company_list_search',
  description:
    'Set the companies tree search box. This does not return companies — read them from context.company_list.items after it resolves.',
  parameters: {
    type: 'object',
    properties: {
      search: { type: 'string', description: 'Search companies by name. Empty clears the search.' },
    },
    required: ['search'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { search?: string };
    searchQuery.value = args.search ?? '';
    await getCompanies();
    return { success: true, search: searchQuery.value };
  },
};

const gotoCompany: AutopilotFunctionDefinition = {
  name: 'goto_company',
  description: 'Select a company in the sidebar and open its detail form. Does not create or save anything.',
  parameters: {
    type: 'object',
    properties: {
      company_id: { type: 'number', description: 'The ID of the company to open' },
    },
    required: ['company_id'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { company_id?: number };
    if (!args.company_id) return { success: false, error: 'company_id is required' };
    await pushAndWait(router, { name: 'settings.company.details', params: { id: args.company_id } });
    return { success: true, company_id: args.company_id };
  },
};

const createCompanyFn: AutopilotFunctionDefinition = {
  name: 'create_company',
  description: 'Create a new company immediately with the given details, then open it.',
  sensitive: true,
  parameters: {
    type: 'object',
    properties: companyFields,
    required: ['name'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as Partial<ICompany>;
    try {
      const response = await createCompany(args);
      await getCompanies();
      await pushAndWait(router, { name: 'settings.company.details', params: { id: response.data.id } });
      return { success: true, company: response.data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create company' };
    }
  },
};

useAutopilotFunctions([setCompanySearch, gotoCompany, createCompanyFn]);
</script>
<style>
.company-block {
  display: flex;
  padding: 16px 4px;
}

.company-block .company-logo {
  flex-shrink: 0;
  background-size: contain;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.company-block .company-logo-placeholder {
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Companies hierarchy tab */
.companies-tree .tree-node-inner {
  padding-left: 16px;
  padding-right: 16px;
}

.companies-tree .tree-node--with-tree-line .tree-node-inner {
  padding-left: 32px;
}

.companies-tree .tree-node--with-tree-line {
  padding-left: 40px;
}

.companies-tree .tree-level-1 {
  padding-left: 0 !important;
}

.companies-tree .tree-level-1 > .tree-node-inner {
  padding-left: 16px;
}

.companies-tree .tree-vline {
  height: 50%;
}

.companies-tree .tree-vline,
.companies-tree .tree-hline {
  background-color: rgba(0, 0, 0, 0.1);
}

.companies-tree .tree-vline:not(:first-of-type) {
  display: none;
}

.companies-tree .tree-node--with-tree-line::before {
  /* Add Border to each node */
  content: '';
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  /* Border thickness */
  background-color: rgba(0, 0, 0, 0.1);
  /* Border color */
}

.companies-tree .tree-node--with-tree-line {
  position: relative;
}

.companies-tree .tree-node {
  cursor: pointer;
}

.companies-tree .tree-node:hover,
.companies-tree .tree-node:has(.company-block.active) {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
